import { BookingSplitRow, Report, ServiceItem, ServiceTransaction } from '../types';
import { selectInspectionSendServices } from './billingSelection';
import { bookingIsFlatProof, inspectionFlatTarget, isFlatRateService, planFlatProofEntry, resolveFlatCoverage } from './flatRateLogic';
import { isNepService } from './nepPosition';
import { isHourlyUnit } from './projectLv';
import { hatBestandteile } from './bestandteile/bestandteilLogic';
import { berichtsbezugVon, berlinZeitpunkt, buchungstag, isFlatProofEntry, isManualPosting, isPostingEntry } from './serviceLogUtils';

/**
 * Pure booking logic for "report sent" — extracted from EmailModal so that the
 * send dialog and the app-start resume path (pendingSendPersistence) apply
 * exactly the same rules.
 *
 * Idempotency: every booking is tagged with a stable reportRef; applying the
 * same booking twice must not increment quantityUsed again or push a second
 * serviceLog entry. The description match keeps entries created before
 * reportRef existed from being double-booked.
 */
export interface ReportBookingOpts {
  serviceId?: string | null;
  /**
   * Aufteilung EINER Begehung auf mehrere LV-Positionen (Lose). Vorrangig vor
   * `serviceId`/`hours`; fehlt das Feld (Altbestand, wiederaufgenommene
   * Versand-Marker aus dem Speicher), gilt weiterhin die einzelne Position.
   * Siehe resolveMainBookings zur Mengenregel.
   */
  mainBookings?: BookingSplitRow[];
  additionalAddons?: Record<string, number>;
  hours?: number | null;
  performerId: string;
  performerName: string;
  inspectionNumber: number;
  sectionId?: string | null;
  date?: string;
  time?: string;
}

export type BookingSkipReason =
  | 'same_report'
  | 'same_report_other_position'
  | 'same_day_same_section'
  | 'no_booking_target'
  // Zusatzleistung auf eine Paketposition (Plan Bestandteile, Kernregel 7) —
  // wird nicht gebucht, der Hinweis sagt warum.
  | 'package_position';

/** Übersprungene Buchung — bewusst sichtbar, siehe Kommentar an sameDayMainBooking. */
export interface BookingSkipNotice {
  /** Fehlt bei 'no_booking_target' — dort gibt es gerade keine Position. */
  serviceId?: string;
  reason: BookingSkipReason;
  /** Fertiger deutscher Hinweistext für die UI. */
  message: string;
}

/**
 * Hauptbuchung oder Zusatzleistung — die Aussage, die NUR hier zweifelsfrei
 * feststeht.
 *
 * Der `reportRef` steht auf BEIDEN Arten und beweist deshalb keine Begehung;
 * genau daraus entsteht die dokumentierte Fehlerklasse „gebucht wurde nur die
 * Zusatzleistung, die Begehung selbst nicht". Wer die Zeilen weiterreicht
 * (Buchungsjournal), gibt die Aussage mit, statt sie aus dem Text zu erraten.
 *
 * Strukturgleich zu `JournalEntryStatement` in serviceJournal.ts — bewusst
 * nicht von dort importiert, damit dieses Modul rein bleibt (kein Netz, kein
 * Supabase-Client).
 */
export interface ReportBookingStatement {
  isAddon: boolean;
  coversInspection?: boolean;
}

export interface ReportBookingResult {
  report: Report;
  skipped: BookingSkipNotice[];
  /**
   * Die in DIESEM Aufruf neu entstandenen Protokollzeilen — leer, wenn ein
   * Riegel gegriffen hat. Wer sie weiterschreibt, braucht genau sie und nie
   * das ganze Protokoll; sie hier mitzugeben erspart der Aufrufstelle einen
   * Vergleich vorher/nachher, der bei gleichen Zeilen daneben liegen kann.
   */
  entries: ServiceTransaction[];
  /** Die Aussage je `ServiceTransaction.id` aus `entries`. */
  statements: Record<string, ReportBookingStatement>;
  /**
   * Hauptbuchungszeilen, die auf die Monatspauschale umgelenkt wurden (siehe
   * redirectMainBookingsToFlat) — fehlt, wenn nichts umgelenkt wurde. Eine
   * Aussage ueber den PLAN: ob die Nachweiszeile dann entstand oder ein
   * Riegel griff, sagen `entries` und `skipped`.
   */
  redirectedToFlat?: FlatRedirect;
}

/** Welche Positionen auf welche Pauschale umgelenkt wurden. */
export interface FlatRedirect {
  fromServiceIds: string[];
  flatServiceId: string;
}

// Tag und Uhrzeit der Begehung als Zeitpunkt in Europe/Berlin
// (`berlinZeitpunkt`, S5d) — derselben Zone, in der der Tagesriegel unten den
// Tag liest. Bis S5d Geraetezeit; im Inland dasselbe Ergebnis. Nur bei
// unerwarteter Form der alte Weg.
const combinedDateIso = (date?: string, time?: string) =>
  date && time
    ? (berlinZeitpunkt(date, time) ?? new Date(`${date}T${time}:00`).toISOString())
    : date
      ? new Date(date).toISOString()
      : new Date().toISOString();

const MAIN_BOOKING_DESC = /^Bericht #(\d+) abgeschlossen$/;

/** Der stabile Buchungsschlüssel eines Berichts. Eine Stelle, ein Format. */
export const reportRefOf = (inspectionNumber: number, sectionId?: string | null): string =>
  `RPT-${sectionId || 'all'}-${inspectionNumber}`;

/**
 * Trägt das Protokoll bereits eine Hauptbuchung dieses Berichts?
 *
 * Absichtlich ohne Bezug auf eine bestimmte Position: die Frage ist nicht
 * "wurde Position X gebucht", sondern "ist diese Begehung überhaupt in der
 * Abrechnung angekommen".
 *
 * S5c: je Zeile entscheidet der Berichtsbezug aus dem Journal, wenn er bekannt
 * ist (`berichtsbezugVon`), sonst wie bisher Ref und Text. Großzügig bleibt es
 * in beiden Wegen — auch eine Zusatzleistung mit dem Ref des Berichts zählt
 * (Wiederholungsversand-Riegel, Begründung an `isInspectionBilled`).
 *
 * Die Spalten fragen nur die NUMMER und schließen Kind-Zeilen aus — den
 * Abschnitt fragen sie bewusst nicht. Die Berichtsnummer ist projektweit
 * (useReportActions: `max(dbCurrentNr, meta.inspectionNumber) + 1`,
 * `projects.current_inspection_number`); `sections[].currentInspectionNumber`
 * zählt nur für die Anzeige. Der Abschnitt einer Buchung und der eines
 * späteren Aufrufs können dagegen auseinanderlaufen („Bearbeiten" setzt
 * `sectionId` auf null, ProjectHub führt keinen aktiven Abschnitt). Ein
 * Abschnittsvergleich meldete dann einen gebuchten Bericht als ungebucht —
 * und die Rückfrage „Erneut abrechnen" lüde zur zweiten Buchung auf ein
 * anderes Los ein. Gegenüber dem Text heißt das:
 *  - WEITER: eine Zeile mit Ref eines anderen Abschnitts zählt (der
 *    Ref-Vergleich verlangte den gleichen) — derselbe Bericht, andere Angabe.
 *  - ENGER: eine Kind-Zeile (`RPT-CHILD-…`) zählt nie für einen eigenen
 *    Bericht, auch wenn ihr Text „Bericht #N abgeschlossen" lautet.
 * Gemessen in Nr. 119 (Abnahme, Beobachtungen 7–11).
 *
 * Ist das Register noch nicht geladen (App-Start, Nachholen beim Start), gilt
 * der Text — die Antwort vor S5c.
 */
export const isReportBooked = (
  serviceLog: Array<{ id?: string; reportRef?: string; description?: string; bookingReference?: string; quantity?: number }> | undefined,
  inspectionNumber: number,
  sectionId?: string | null,
): boolean => {
  const ref = reportRefOf(inspectionNumber, sectionId);
  const desc = `Bericht #${inspectionNumber} abgeschlossen`;
  return (serviceLog || []).some(e => {
    if (!e) return false;
    const bezug = berichtsbezugVon(e);
    if (bezug) return bezug.nummer === inspectionNumber && bezug.kindProjekt === null;
    return e.reportRef === ref || e.description === desc;
  });
};

/**
 * Die Berichtsnummern, zu denen im Protokoll eine Hauptbuchung steht.
 *
 * Grundlage für den Abgleich "archivierter Bericht ohne Buchung" im
 * Projektcheck. Beide Erkennungswege zählen, weil Einträge aus der Zeit vor
 * dem reportRef nur die Beschreibung tragen.
 *
 * S5c: ist der Berichtsbezug der Zeile aus dem Journal bekannt, zählt dessen
 * Nummer — dieselbe, die Ref oder kanonischer Text liefern (Kind-Refs zählen
 * wie bisher mit, der Ref-Vergleich unten nimmt jede `RPT-…`-Form).
 */
export const bookedReportNumbers = (
  serviceLog: Array<{ id?: string; reportRef?: string; description?: string; bookingReference?: string; quantity?: number }> | undefined,
): Set<number> => {
  const out = new Set<number>();
  for (const entry of serviceLog || []) {
    const bezug = berichtsbezugVon(entry);
    if (bezug) {
      out.add(bezug.nummer);
      continue;
    }
    const fromDesc = MAIN_BOOKING_DESC.exec(String(entry?.description || ''))?.[1];
    if (fromDesc) out.add(Number(fromDesc));
    const fromRef = /^RPT-.*-(\d+)$/.exec(String(entry?.reportRef || ''))?.[1];
    if (fromRef) out.add(Number(fromRef));
  }
  return out;
};

const formatDay = (ymd: string | null): string => {
  if (!ymd) return 'unbekanntem Datum';
  const [y, m, d] = ymd.split('-');
  return `${d}.${m}.${y}`;
};

/**
 * Welche Hauptpositionen diese Begehung bucht — und mit welcher Menge.
 *
 * Eine Begehung kann mehrere LOSE abdecken. Es entsteht genau ein Bericht, die
 * Leistung muss aber je Los auf eine eigene LV-Position gebucht werden.
 *
 * DIE MENGENREGEL, identisch zu der im Dialog (billingSelection.ts):
 *  - EINE Position (der Normalfall und der gesamte Altbestand): die Menge folgt
 *    der EINHEIT — eine Stundenposition bucht die erfassten Stunden, eine
 *    Stueckposition genau 1. Das ist byte-genau das alte Verhalten.
 *  - MEHRERE Positionen (Aufteilung): jede Zeile bucht die EINGETRAGENE Menge,
 *    unabhaengig von der Einheit. Sonst liesse sich eine Stueckposition nicht
 *    auf zwei Lose verteilen — genau darum geht es beim Aufteilen.
 *
 * `isHourlyUnit` ist dabei die einzige Stundenerkennung der Anwendung; ein
 * exakter Vergleich auf 'Stunden' buchte bei 'Std'/'h' still 1 statt der
 * tatsaechlichen Stunden.
 */
/**
 * EXPORTIERT, damit das Nachbuchen (reportRebookLogic.ts) dem Bearbeiter
 * VORHER dieselbe Menge zeigt, die gleich gebucht wird. Zwei Fassungen der
 * Mengenregel waeren genau die Sorte Abweichung, die niemand bemerkt, bis eine
 * Stundenposition mit 1 statt mit den Stunden auf der Rechnung steht.
 */
export const resolveMainBookings = (opts: ReportBookingOpts, services: ServiceItem[]): BookingSplitRow[] => {
  const withUnitRule = (serviceId: string, entered: number): BookingSplitRow => {
    const svc = services.find(s => s.id === serviceId);
    return { serviceId, quantity: isHourlyUnit(svc?.unit) ? entered : 1 };
  };

  const split = (opts.mainBookings || []).filter(r => r && r.serviceId && (r.quantity ?? 0) > 0);
  if (split.length > 1) return split.map(r => ({ serviceId: r.serviceId, quantity: r.quantity }));
  if (split.length === 1) return [withUnitRule(split[0].serviceId, split[0].quantity)];
  if (opts.serviceId) return [withUnitRule(opts.serviceId, opts.hours ?? 1)];
  return [];
};

/**
 * Die WEITE Begehungsdefinition — dieselbe wie `isInspectionCoverageTarget` in
 * flatRateLogic (dort nicht exportiert, Kern-API unveraendert). Lockstep: aendert
 * sich die Regel dort, aendert sie sich hier.
 */
const istBegehungsposition = (s: ServiceItem): boolean =>
  s.assignment === 'inspection' ||
  !!(s.lvPosition && (s.lvPosition.startsWith('2.7.') || s.lvPosition.startsWith('2.8.'))) ||
  (s.title || '').toLowerCase().includes('begehung');

/**
 * SICHERHEITSNETZ im Pauschalprojekt: Hauptbuchungszeilen auf eine von der
 * Begehungspauschale gedeckte Begehungsposition gehen auf die Pauschale.
 *
 * Warum hier und nicht nur in der Auswahlliste (selectBillableServices): es gibt
 * Wege an der Liste vorbei — precapturedServiceId/-Bookings, der gespeicherte
 * currentBillingSplit, Versand-Marker von vor dem Deploy (pendingSendPersistence),
 * ein Nachbuch-Ziel auf 2.7.10. Jeder davon buchte sonst weiter eine Menge auf
 * die Begehungsposition, die im Pauschalprojekt nichts mehr bekommt.
 *
 * Umgelenkt wird nur, was GENAU diese Pauschale deckt und eine Begehung ist —
 * unter einer 'all'-Pauschale bleibt eine andere gedeckte Position, unter einer
 * 'explicit'-Pauschale eine nicht gelistete Begehungsposition unveraendert.
 * Mehrere Zeilen auf dieselbe Pauschale (Aufteilung auf Lose) sind EIN Vorgang
 * und werden still zu EINER Zeile — ein Nachweis hat keine Menge, die sich
 * aufteilen liesse. Eine NEP-Pauschale ist kein Ziel (wie in
 * selectBillableServices). Zusatzleistungen laufen hier nicht durch.
 */
export const redirectMainBookingsToFlat = (
  rows: BookingSplitRow[],
  services: ServiceItem[],
): { rows: BookingSplitRow[]; redirectedToFlat?: FlatRedirect } => {
  const flat = inspectionFlatTarget(services.filter(s => !isNepService(s)));
  if (!flat) return { rows };
  const coverage = resolveFlatCoverage(services);

  const out: BookingSplitRow[] = [];
  const from: string[] = [];
  let flatRowSeen = false;
  let flatRowRedirected = false;
  for (const row of rows) {
    const svc = services.find(s => s.id === row.serviceId);
    const umlenken = !!svc && coverage.get(row.serviceId) === flat.id && istBegehungsposition(svc);
    if (umlenken) from.push(row.serviceId);
    if (!umlenken && row.serviceId !== flat.id) {
      out.push(row);
      continue;
    }
    // Eine direkt ZWEIMAL gewaehlte Pauschale bleibt beim alten Weg (der
    // Ref-Riegel meldet die zweite als same_report); nur wo umgelenkt wurde,
    // ist das Zusammenfassen derselbe Vorgang.
    if (flatRowSeen && (umlenken || flatRowRedirected)) continue;
    flatRowSeen = true;
    flatRowRedirected = flatRowRedirected || umlenken;
    out.push({ serviceId: flat.id, quantity: row.quantity });
  }
  return from.length > 0
    ? { rows: out, redirectedToFlat: { fromServiceIds: from, flatServiceId: flat.id } }
    : { rows: out };
};

/**
 * Anzeigename der Pauschale, auf die eine Buchung auf `serviceId` umgelenkt
 * wuerde — undefined, wenn nicht umgelenkt wird. Fuer den Hinweis in der
 * Schnellbuchung (ServiceTrackingModal): dieselbe Regel wie beim Versand.
 */
export const flatRedirectTitleFor = (services: ServiceItem[], serviceId: string): string | undefined => {
  const umlenkung = redirectMainBookingsToFlat([{ serviceId, quantity: 1 }], services).redirectedToFlat;
  if (!umlenkung) return undefined;
  const flat = services.find(s => s.id === umlenkung.flatServiceId);
  return [flat?.lvPosition, flat?.title].filter(Boolean).join(' ') || 'Monatspauschale';
};

/**
 * Geht die Begehung, fuer die `serviceId` gewaehlt ist, als NACHWEIS auf eine
 * Pauschale — und auf welche? null = alter Weg, Mengenbuchung auf der Position.
 *
 * Fuer die Wege, die nicht ueber applyReportBookingWithNotices laufen (die
 * Tochter bucht direkt ins LV der Mutter; der Folgebericht erbt die Position).
 * Dieselbe Regel wie dort, damit ein zweites, von der Pauschale NICHT
 * gedecktes Los ('explicit' mit nur Los A) buchbar bleibt:
 *  - gewaehlt ist eine Pauschale: auf sie — eine Pauschale nimmt nur Nachweise
 *    an (bookingIsFlatProof), eine Menge darauf waere ein Monat Geld ohne
 *    Monatsschluessel;
 *  - gewaehlt ist eine Position, die die Begehungspauschale deckt: auf die
 *    Pauschale (redirectMainBookingsToFlat);
 *  - jede andere bekannte Position (Los B): alter Weg;
 *  - nichts gewaehlt oder Id unbekannt (Position geloescht, fremdes LV): auf
 *    die Pauschale nur, wenn der Versanddialog neben ihr keine ungedeckte
 *    Begehungsposition anboete — sonst waere unklar, welches Los gemeint war,
 *    und es bleibt beim alten Weg.
 */
export const flatTargetForInspectionChoice = (
  services: ServiceItem[],
  serviceId: string | null | undefined,
): string | null => {
  const gewaehlt = serviceId ? services.find(s => s.id === serviceId) : undefined;
  if (gewaehlt) {
    if (bookingIsFlatProof(gewaehlt)) return gewaehlt.id;
    return redirectMainBookingsToFlat([{ serviceId: gewaehlt.id, quantity: 1 }], services)
      .redirectedToFlat?.flatServiceId ?? null;
  }
  const flat = inspectionFlatTarget(services.filter(s => !isNepService(s)));
  if (!flat) return null;
  const ungedeckt = selectInspectionSendServices(services).some(s => !isFlatRateService(s));
  return ungedeckt ? null : flat.id;
};

/**
 * Die Abrechnungsposition, die ein Folgebericht erbt ("Nächste Begehung
 * anlegen"): die bisherige, sonst die erste Begehungsposition — im
 * Pauschalprojekt umgelenkt nach flatTargetForInspectionChoice. Eine
 * gedeckte Position wird zur Pauschale, ein ungedecktes Los bleibt.
 *
 * Ohne Pauschale byte-gleich zum alten Weg: auch eine Id, die es nicht mehr
 * gibt, bleibt stehen.
 */
export const followUpBillingServiceId = (
  services: ServiceItem[],
  previousId: string | null | undefined,
): string | undefined => {
  const kandidat = previousId || services.find(s => s.assignment === 'inspection')?.id;
  return flatTargetForInspectionChoice(services, kandidat) ?? kandidat ?? undefined;
};

export const applyReportBookingWithNotices = (report: Report, opts: ReportBookingOpts): ReportBookingResult => {
  const services = [...(report.metadata.globalData?.services || [])];
  const serviceLog = [...(report.metadata.globalData?.serviceLog || [])];
  // Stand VOR diesem Lauf — fuer den Bestandsschutz der Nachweiszeile
  // (hauptbuchungAuf unten). serviceLog waechst waehrend des Laufs.
  const protokollVorLauf: ServiceTransaction[] = [...serviceLog];
  const skipped: BookingSkipNotice[] = [];

  const sectionKey = opts.sectionId || 'all';
  const reportRef = `RPT-${sectionKey}-${opts.inspectionNumber}`;
  const mainDesc = `Bericht #${opts.inspectionNumber} abgeschlossen`;
  const addonDesc = `Zusatzleistung: Bericht #${opts.inspectionNumber}`;
  const isAlreadyBooked = (serviceId: string) => serviceLog.some((e: any) =>
    e.serviceId === serviceId && (e.reportRef === reportRef || e.description === mainDesc || e.description === addonDesc)
  );

  // Zweiter Dedup-Schlüssel: BEGEHUNGSTAG + Abschnitt.
  //
  // Warum: der reportRef-Guard oben greift nur bei identischer Nummer. Genau die
  // ändert sich aber im Schadensfall — wirkt der Versand gescheitert, legt der
  // Nutzer über "Begehung anlegen" eine neue Nummer an und verschickt denselben
  // Inhalt erneut; die Begehung wird dann ein zweites Mal abgerechnet.
  //
  // Trade-off, bewusst so entschieden: eine echte zweite Begehung desselben
  // Abschnitts am selben Tag wird dadurch nicht mehr automatisch gebucht. Der
  // Doppelbuchungs-Schaden (falsche Rechnung an den Kunden, Korrektur nur über
  // Storno) wiegt schwerer als der Aufwand einer manuellen Nachbuchung. Damit
  // dieser Fall nicht still verschwindet, wird er NICHT stumm übersprungen,
  // sondern als BookingSkipNotice zurückgegeben und in der Oberfläche angezeigt.
  //
  // AUFTEILUNG AUF MEHRERE LOSE — was der Riegel seit der Aufteilung genau tut:
  //  - Er zählt nur Hauptbuchungen mit einer ANDEREN Berichtsnummer
  //    (`desc === mainDesc` fällt heraus). Ein Bericht darf deshalb mehrere
  //    Hauptbuchungen auf VERSCHIEDENEN Positionen erzeugen — das ist die
  //    Aufteilung, und sie ist genau ein Vorgang, kein zweiter. Steht der
  //    Bericht aus einem FRÜHEREN Vorgang schon auf einer anderen Position,
  //    sperrt `hauptbuchungAndererPosition` (unten, seit 05.10.2026).
  //  - Dieselbe Position ein zweites Mal zu buchen bleibt gesperrt: dafür greift
  //    isAlreadyBooked, das je Zeile gegen den MITWACHSENDEN serviceLog geprüft
  //    wird (jede gebuchte Zeile ist für die folgenden sofort sichtbar).
  //  - Am Riegel selbst wird NICHTS aufgeweicht: eine Hauptbuchung eines anderen
  //    Berichts am selben Tag und im selben Abschnitt blockiert weiterhin JEDE
  //    Position, auch eine bisher ungebuchte. Genau dieser Fall war der
  //    dokumentierte Doppelbuchungs-Vorfall.
  //
  // Deshalb eine Funktion statt eines einmal berechneten Wertes: die Prüfung
  // läuft je Zeile gegen den laufenden Stand des Protokolls.
  // Der Tag in Europe/Berlin (`buchungstag`, S5d) — derselbe, den das Journal
  // als `entry_day` fuehrt. Bis S5d rechnete dieser Riegel in Geraetezeit;
  // im Inland dasselbe, auf einem Geraet in anderer Zone nicht.
  const bookingDay = buchungstag(opts.date);
  const findSameDayMainBooking = () => (bookingDay
    ? serviceLog.find((e: any) => {
        const desc = String(e?.description || '');
        if (!MAIN_BOOKING_DESC.test(desc) || desc === mainDesc) return false;
        // Alteinträge ohne reportRef zählen mit — sie stammen aus derselben
        // Begehungsbuchung, nur vor Einführung des Refs.
        if (typeof e?.reportRef === 'string' && !e.reportRef.startsWith(`RPT-${sectionKey}-`)) return false;
        return buchungstag(e?.date) === bookingDay;
      })
    : undefined);

  // Die neuen Zeilen dieses Vorgangs samt Aussage — mitgefuehrt, nicht
  // hinterher rekonstruiert.
  const booked: ServiceTransaction[] = [];
  const statements: Record<string, ReportBookingStatement> = {};

  const book = (serviceId: string, quantity: number, description: string, statement: ReportBookingStatement) => {
    const sIndex = services.findIndex(s => s.id === serviceId);
    if (sIndex === -1) return;
    const s = services[sIndex];
    // Pauschale: nur eine Nachweiszeile (Menge 0, NW-…), die Position bleibt
    // wie sie ist. Ein hochgezaehltes quantityUsed waere ein Monat Geld ohne
    // Monatsschluessel — Monate bucht allein planFlatMonthBooking (FLAT-JJJJ-MM).
    // Ref und Text sind die eines normalen Versands, damit Ref-/Text-Riegel
    // und Tagesriegel oben die Zeile genauso sehen wie eine Mengenbuchung.
    if (bookingIsFlatProof(s)) {
      const proof = planFlatProofEntry({
        id: crypto.randomUUID(),
        flatServiceId: serviceId,
        date: combinedDateIso(opts.date, opts.time),
        bookedAt: new Date().toISOString(),
        performerId: opts.performerId,
        performerName: opts.performerName,
        description,
        reportRef,
      });
      serviceLog.push(proof);
      booked.push(proof);
      statements[proof.id] = statement;
      return;
    }
    const newUsed = (s.quantityUsed || 0) + quantity;
    const isDone = s.quantityContracted > 0 && newUsed >= s.quantityContracted;
    services[sIndex] = { ...s, quantityUsed: newUsed, status: isDone ? 'done' : s.status };
    const entry: ServiceTransaction = {
      id: crypto.randomUUID(),
      date: combinedDateIso(opts.date, opts.time),
      bookedAt: new Date().toISOString(),
      serviceId,
      quantity,
      performerId: opts.performerId,
      performerName: opts.performerName,
      isSubstitution: false,
      description,
      reportRef,
    };
    serviceLog.push(entry);
    booked.push(entry);
    statements[entry.id] = statement;
  };

  // KEIN Buchungsziel — der gefährlichste Fall, weil bisher nichts geschah und
  // niemand es erfuhr: Mail, Archiv und Sendebericht entstehen, die Leistung
  // fehlt. Sie fällt erst Wochen später bei der Rechnung auf, und dann ist der
  // Bericht längst versendet (Vorfall 26041014, Bericht #13 vom 31.07.2026:
  // archiviert, zweifach versendet, nie gebucht → 160 € lagen still auf keinem
  // Beleg).
  //
  // Ohne Position kommt resolveMainBookings mit einer leeren Liste zurück und
  // die Schleife unten läuft ins Leere — es gäbe also nicht einmal einen
  // übersprungenen Eintrag zu melden. Deshalb hier.
  //
  // Nur wenn für diesen Bericht wirklich noch nichts gebucht ist: ein
  // Wiederholungsversand eines bereits abgerechneten Berichts bucht bewusst
  // nichts mehr und ist kein Vorfall.
  const { rows: mainBookings, redirectedToFlat } = redirectMainBookingsToFlat(resolveMainBookings(opts, services), services);
  if (mainBookings.length === 0 && !isReportBooked(serviceLog, opts.inspectionNumber, opts.sectionId)) {
    skipped.push({
      reason: 'no_booking_target',
      message: `Für Bericht #${opts.inspectionNumber} wurde KEINE Leistung gebucht — es war keine Position zur Abrechnung ausgewählt. Die Begehung erscheint damit auf keiner Rechnung. Bitte in der Leistungserfassung nachbuchen.`,
    });
  }

  // Jede Zeile der Aufteilung ist eine eigene Hauptbuchung mit derselben
  // Beschreibung und demselben reportRef — sie gehören zu EINER Begehung.
  // Welche Positionen deckt welche Pauschale — fuer den Bestandsschutz unten.
  const deckung = resolveFlatCoverage(services);
  // Eine HAUPTbuchung dieses Berichts auf einer Position (keine Zusatzleistung
  // mit demselben Ref): nur sie belegt die Begehung schon.
  //
  // Gegen den Stand VOR dem Lauf, nicht gegen das mitwachsende Protokoll: eine
  // Zeile DIESES Laufs auf einer gedeckten Nicht-Begehung (unter 'all' bleibt
  // sie Mengenbuchung, siehe redirectMainBookingsToFlat) traegt denselben Text
  // und Ref und unterdrueckte sonst den Nachweis der Begehung, die in der
  // Aufteilung nach ihr kommt (Opus-Review 30.09.).
  const hauptbuchungAuf = (serviceId: string) => protokollVorLauf.some((e: ServiceTransaction) =>
    e.serviceId === serviceId
    && (e.description === mainDesc
      || (e.reportRef === reportRef && !String(e.description || '').startsWith('Zusatzleistung'))));

  // Derselbe Bericht steht aus einem FRÜHEREN Vorgang schon auf einer ANDEREN
  // Position (Betreiber 05.10.2026). Vorfall 6051 #149: das Fix-SQL buchte die
  // Begehung auf 2.7.60, der Nachhol-Marker auf Rolfs Gerät danach mit seiner
  // Auswahl aus dem Versanddialog auf 2.7.20. `isAlreadyBooked` fragt nur die
  // eigene Position, der Tagesriegel schließt denselben Bericht aus — eine
  // Pauschale zu viel auf der Rechnung, und keine Sperre sah es.
  //
  // Gegen den Stand VOR dem Lauf: die Zeilen DIESES Laufs sind die Aufteilung
  // und bleiben erlaubt. Wer später ein Los ergänzen will, bucht es von Hand.
  //
  // Was zählt: eine Hauptbuchung desselben Berichts (Text oder Ref, wie in
  // `hauptbuchungAuf`) mit Menge — oder eine Nachweiszeile, die auf der
  // Pauschale die Begehung ist. Was nicht zählt: Zusatzleistungen (gebucht
  // war dann nur der Zuschlag, die Begehung fehlt wirklich),
  // Buchungsvorgänge, und Zeilen auf einer Position, die es nicht mehr gibt —
  // ohne Position hält diese Zeile keine Forderung, und die Sperre ließe
  // sich nicht auflösen.
  const hauptbuchungAndererPosition = (serviceId: string): ServiceTransaction | undefined =>
    protokollVorLauf.find((e: ServiceTransaction) =>
      !!e
      && !!e.serviceId
      && e.serviceId !== serviceId
      && services.some(s => s.id === e.serviceId)
      && !isPostingEntry(e)
      // Ausdrücklich, auch wenn der Rückfall von isPostingEntry sie kennt:
      // ein Abrechnungsstempel ist keine Begehung, egal was das Register sagt.
      && !isManualPosting(e)
      && !String(e.description || '').startsWith('Zusatzleistung')
      && (e.description === mainDesc || e.reportRef === reportRef)
      && ((Number(e.quantity) || 0) > 0 || isFlatProofEntry(e)));
  const positionsName = (serviceId?: string): string => {
    const s = services.find(x => x.id === serviceId);
    return [s?.lvPosition, s?.title].filter(Boolean).join(' ') || 'einer anderen Position';
  };

  mainBookings.forEach(row => {
    const svc = services.find(s => s.id === row.serviceId);
    // Nachweis auf der Pauschale: auch ein Bestand auf einer von IHR gedeckten
    // Position zaehlt — ob umgelenkt oder die Pauschale direkt gewaehlt
    // (Codex-Befund 30.09.). Ein vor dem Deploy auf 2.7.10 gebuchter Bericht,
    // erneut versendet, bekaeme sonst zusaetzlich eine Nachweiszeile — zwei
    // Begehungsnachweise fuer einen Bericht.
    const gedeckteVorher = svc && bookingIsFlatProof(svc)
      ? [...deckung.entries()].filter(([, flatId]) => flatId === svc.id).map(([id]) => id)
      : [];
    if (isAlreadyBooked(row.serviceId) || gedeckteVorher.some(hauptbuchungAuf)) {
      // Erwarteter Idempotenz-Fall (Wiederholung derselben Buchung, z. B. beim
      // Nachholen nach App-Neustart) — kein Hinweis, das ist kein Vorfall.
      skipped.push({
        serviceId: row.serviceId,
        reason: 'same_report',
        message: `Bericht #${opts.inspectionNumber} ist bereits abgerechnet — Buchung übersprungen.`,
      });
      return;
    }
    const andere = hauptbuchungAndererPosition(row.serviceId);
    if (andere) {
      const dort = positionsName(andere.serviceId);
      skipped.push({
        serviceId: row.serviceId,
        reason: 'same_report_other_position',
        message: `Bericht #${opts.inspectionNumber} ist bereits auf ${dort} abgerechnet — die Buchung auf ${positionsName(row.serviceId)} wurde übersprungen, damit die Begehung nicht zweimal in Rechnung geht. Ist ${dort} die falsche Position, dort „Buchung zurücknehmen" und neu buchen. War es tatsächlich eine zweite Leistung, bitte manuell in der Leistungserfassung nachbuchen.`,
      });
      return;
    }
    const sameDayMainBooking = findSameDayMainBooking();
    if (sameDayMainBooking) {
      const otherNr = String((sameDayMainBooking as any).description || '').match(MAIN_BOOKING_DESC)?.[1];
      skipped.push({
        serviceId: row.serviceId,
        reason: 'same_day_same_section',
        message: `Die Begehung am ${formatDay(bookingDay)} ist bereits über Bericht #${otherNr ?? '?'} abgerechnet — diese Buchung wurde übersprungen, damit die Leistung nicht doppelt in Rechnung geht. War es tatsächlich eine zweite Begehung an diesem Tag, bitte manuell in der Leistungserfassung nachbuchen.`,
      });
      return;
    }
    // Hauptbuchung der Begehung. Gedeckt ist sie nur mit einer Menge > 0 —
    // dieselbe Grenze, die die Bestandsuebernahme aus dem Text zieht. Die
    // Nachweiszeile auf einer Pauschale deckt trotz Menge 0: sie IST die
    // Begehung dieses Berichts (inferInspectionCoverage erschliesst dasselbe).
    if (svc) {
      book(row.serviceId, row.quantity, mainDesc, {
        isAddon: false,
        coversInspection: row.quantity > 0 || bookingIsFlatProof(svc),
      });
    }
  });

  Object.entries(opts.additionalAddons || {}).forEach(([addonId, quantity]) => {
    if (!quantity || quantity <= 0) return;
    // Zusatzleistungen bleiben beim reportRef-Guard: sie sind pro Bericht frei
    // wählbar und nicht an "eine Begehung pro Tag" gebunden.
    if (isAlreadyBooked(addonId)) return;
    // ZWEITE SPERRE (Plan Bestandteile, Kernregel 7): eine Paketposition ist
    // nie eine Zusatzleistung. Der Dialog bietet sie nicht an
    // (isAdditionalServiceCandidate); kommt sie trotzdem an — ein gespeicherter
    // Versand-Marker von vorher, ein Paket, das inzwischen Bestandteile
    // bekam —, wird NICHT gebucht, und der Hinweis sagt, wo sie gebucht wird.
    const paket = services.find(s => s.id === addonId);
    if (paket && hatBestandteile(paket)) {
      skipped.push({
        serviceId: addonId,
        reason: 'package_position',
        message: `„${positionsName(addonId)}" ist eine Paketposition mit Bestandteilen und wurde nicht als Zusatzleistung gebucht. `
          + 'Sie wird gebucht, sobald alle Bestandteile erledigt sind (Liste der Bestandteile in der Position).',
      });
      return;
    }
    book(addonId, quantity, addonDesc, { isAddon: true });
  });

  return {
    report: {
      ...report,
      metadata: {
        ...report.metadata,
        isExported: true,
        globalData: {
          ...report.metadata.globalData,
          services,
          serviceLog,
        },
      },
    } as Report,
    skipped,
    entries: booked,
    statements,
    ...(redirectedToFlat ? { redirectedToFlat } : {}),
  };
};

/** Kompatibler Einstieg für Aufrufer, die nur den Report brauchen. */
export const applyReportBooking = (report: Report, opts: ReportBookingOpts): Report =>
  applyReportBookingWithNotices(report, opts).report;
